import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPrestamosActivos, getPrestamosActivosCompletos, getAsesores, getEstadisticasPrestamosActivos } from '../../../js/prestamosActivosService';
import PrestamosActivosFilters from '../../../components/ui/Admin/PrestamosActivosComponents/PrestamosActivosFilters';
import PrestamosActivosTable from '../../../components/ui/Admin/PrestamosActivosComponents/PrestamosActivosTable';
import PrestamosActivosKPICards from '../../../components/ui/Admin/PrestamosActivosComponents/PrestamosActivosKPICards';
import ReportePrestamosActivos from '../../../components/ui/Admin/PrestamosActivosComponents/ReportePrestamosActivos';
import ReportePrestamosActivosExcel from '../../../components/ui/Admin/PrestamosActivosComponents/ReportePrestamosActivosExcel';
import Paginacion from '../../../components/Reutilizables/Paginacion';
import FetchWithGif from '../../../components/Reutilizables/FetchWithGif';
import {
    SkeletonFilters,
    SkeletonLoansTable,
    SkeletonKPICards,
    EmptyState,
    ErrorState,
} from '../../../components/ui/Admin/PrestamosActivosComponents/LoadingSkeletons/PrestamosActivosSkeletons';

const INITIAL_FILTERS = {
    search: '',
    asesor_id: '',
    abonado_por: '',
    casi_por_terminar: false,
    per_page: 50,
    page: 1,
};

const INITIAL_STATE = {
    loading: false,
    error: '',
    data: [],
    meta: {},
    initialLoad: true,
};

export default function DashboardPrestamosActivosPage() {
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [asesores, setAsesores] = useState([]);
    const [estadisticas, setEstadisticas] = useState(null);
    const [uiErrors, setUiErrors] = useState({});
    const [serverErrorMsg, setServerErrorMsg] = useState('');
    const [state, setState] = useState(INITIAL_STATE);
    const [showPDFModal, setShowPDFModal] = useState(false);
    const [isLoadingExport, setIsLoadingExport] = useState(false);
    
    const pdfDataRef = useRef({ loans: [], url: null, isGenerating: false });
    const excelDataRef = useRef({ loans: [], isGenerating: false });
    const meta = useMemo(() => state?.meta ?? {}, [state?.meta]);
    const data = useMemo(() => state?.data ?? [], [state?.data]);
    const hasData = data.length > 0;
    const isInitialLoading = state.loading && state.initialLoad && !hasData;
    const hasError = Boolean(state.error);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [asesoresResult, estadisticasResult] = await Promise.all([
                    getAsesores(),
                    getEstadisticasPrestamosActivos()
                ]);
                setAsesores(asesoresResult.asesores || asesoresResult.data || []);
                setEstadisticas(estadisticasResult);
            } catch (error) {
                console.error('Error al cargar datos iniciales:', error);
            }
        };
        loadInitialData();
    }, []);

    const handleValidationError = useCallback((payload) => {
        const backendErrors = payload?.errors || {};
        const mappedErrors = Object.keys(backendErrors).reduce((acc, key) => {
            if (backendErrors[key]?.length) {
                acc[key] = backendErrors[key][0];
            }
            return acc;
        }, {});

        setUiErrors(mappedErrors);
        setServerErrorMsg(payload?.message || 'Revise los campos marcados.');
    }, []);

    const fetchData = useCallback(async (isInitialLoad = false) => {
        setState((prevState) => ({
            ...prevState,
            loading: true,
            initialLoad: isInitialLoad
        }));
        setServerErrorMsg('');

        try {
            const result = await getPrestamosActivos(filters);

            setState({
                loading: false,
                error: '',
                data: Array.isArray(result?.data) ? result.data : [],
                meta: result?.meta ?? {},
                initialLoad: false,
            });
            setUiErrors({});
        } catch (error) {
            const status = error?.status || error?.response?.status;
            const payload = error?.response?.data || {};

            if (status === 422) {
                handleValidationError(payload);
                setState((prevState) => ({
                    ...prevState,
                    loading: false,
                    initialLoad: false
                }));
                return;
            }

            setState((prevState) => ({
                ...prevState,
                loading: false,
                error: error?.message || 'No se pudo cargar el reporte de préstamos activos.',
                initialLoad: false,
            }));
        }
    }, [filters, handleValidationError]);

    useEffect(() => {
        fetchData(true);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!state.initialLoad) {
            fetchData(false);
        }
    }, [filters.page]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
        setUiErrors((prev) => ({ ...prev, [name]: '' }));
    }, []);

    const handleApplyFilters = useCallback(() => {
        setUiErrors({});
        setServerErrorMsg('');
        fetchData(true);
    }, [fetchData]);

    const handleResetFilters = useCallback(() => {
        setFilters(INITIAL_FILTERS);
        setUiErrors({});
        setServerErrorMsg('');
    }, []);

    const handlePageChange = useCallback((newPage) => {
        setFilters((prev) => ({ ...prev, page: newPage }));
    }, []);

    const handleGeneratePDF = useCallback(async () => {
        setIsLoadingExport(true);
        try {
            const pdfFilters = {
                search: filters.search,
                asesor_id: filters.asesor_id,
                abonado_por: filters.abonado_por,
                casi_por_terminar: filters.casi_por_terminar,
            };

            const result = await getPrestamosActivosCompletos(pdfFilters);
            const loans = Array.isArray(result?.data) ? result.data : [];
            pdfDataRef.current = {
                loans,
                url: null,
                isGenerating: true
            };
            
            setShowPDFModal(true);
            setIsLoadingExport(false);
        } catch (error) {
            console.error('Error al cargar préstamos para PDF:', error);
            setState((prevState) => ({
                ...prevState,
                error: 'Error al cargar los datos para el PDF. Intente nuevamente.',
            }));
            setIsLoadingExport(false);
        }
    }, [filters.search, filters.asesor_id, filters.abonado_por, filters.casi_por_terminar]);

    const handlePDFGenerated = useCallback((url) => {
        pdfDataRef.current.url = url;
        pdfDataRef.current.isGenerating = false;
        setShowPDFModal(false);
        setTimeout(() => {
            setShowPDFModal(true);
        }, 0);
    }, []);

    const handleClosePDF = useCallback(() => {
        if (pdfDataRef.current.url) {
            URL.revokeObjectURL(pdfDataRef.current.url);
        }
        pdfDataRef.current = { loans: [], url: null, isGenerating: false };
        setShowPDFModal(false);
    }, []);

    const handleGenerateExcel = useCallback(async () => {
        setIsLoadingExport(true);
        excelDataRef.current.isGenerating = true;
        
        try {
            const excelFilters = {
                search: filters.search,
                asesor_id: filters.asesor_id,
                abonado_por: filters.abonado_por,
                casi_por_terminar: filters.casi_por_terminar,
            };

            const result = await getPrestamosActivosCompletos(excelFilters);
            const loans = Array.isArray(result?.data) ? result.data : [];
            
            if (loans.length === 0) {
                setState((prevState) => ({
                    ...prevState,
                    error: 'No hay datos para generar el Excel.',
                }));
                excelDataRef.current.isGenerating = false;
                setIsLoadingExport(false);
                return;
            }

            excelDataRef.current = {
                loans,
                isGenerating: true
            };
            
            setIsLoadingExport(false);
        } catch (error) {
            console.error('Error al cargar préstamos para Excel:', error);
            setState((prevState) => ({
                ...prevState,
                error: 'Error al cargar los datos para el Excel. Intente nuevamente.',
            }));
            excelDataRef.current.isGenerating = false;
            setIsLoadingExport(false);
        }
    }, [filters.search, filters.asesor_id, filters.abonado_por, filters.casi_por_terminar]);

    const handleExcelGenerated = useCallback((url) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = `reporte-prestamos-activos-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        excelDataRef.current = { loans: [], isGenerating: false };
    }, []);

    const handleDismissError = useCallback(() => {
        setState((prevState) => ({ ...prevState, error: '' }));
    }, []);

    const getAsesorName = useCallback(() => {
        if (!filters.asesor_id || asesores.length === 0) return null;
        const asesor = asesores.find(a => String(a.id) === String(filters.asesor_id));
        return asesor?.nombre || filters.asesor_id;
    }, [filters.asesor_id, asesores]);

    if (isInitialLoading) {
        return (
            <div className="min-h-screen bg-gray-50">
            <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
                <header className="mb-6">
                    <div className="h-8 w-80 bg-gray-200 rounded mb-2 animate-pulse"></div>
                    <div className="h-4 w-60 bg-gray-200 rounded animate-pulse"></div>
                </header>
                <SkeletonFilters />
                <SkeletonKPICards />
                <SkeletonLoansTable />
            </div>
            </div>
        );
    }

    if (hasError && !hasData) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <ErrorState
                    title="Error al cargar el reporte"
                    message={state.error}
                    onRetry={() => fetchData(true)}
                />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {state.loading && !state.initialLoad && <FetchWithGif />}
            <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto space-y-6">
                <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex flex-col gap-5 p-6 md:p-7">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-red-500">Seguimiento de cartera</p>
                                <h1 className="mt-2 text-2xl font-bold text-slate-900">
                                    Reporte de préstamos activos
                                </h1>
                                <p className="mt-3 max-w-2xl text-sm text-slate-600">
                                    Visualiza clientes vigentes, identifica casos por terminar y exporta información operativa.
                                </p>
                            </div>
                            {hasData && (
                                <div className="flex flex-wrap gap-3">
                                    <button
                                        onClick={handleGenerateExcel}
                                        disabled={state.loading || isLoadingExport}
                                        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {isLoadingExport ? (
                                            <>
                                                <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                </svg>
                                                Cargando datos...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                Exportar Excel
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={handleGeneratePDF}
                                        disabled={state.loading || isLoadingExport}
                                        className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {isLoadingExport ? (
                                            <>
                                                <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                </svg>
                                                Cargando datos...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                Exportar PDF
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* Filtros */}
                <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
                    <PrestamosActivosFilters
                        filters={filters}
                        onChange={handleFilterChange}
                        onApply={handleApplyFilters}
                        onReset={handleResetFilters}
                        loading={state.loading}
                        errors={uiErrors}
                        formError={serverErrorMsg}
                        asesores={asesores}
                    />
                </section>

                {/* KPI Cards */}
                {estadisticas && <PrestamosActivosKPICards estadisticas={estadisticas} />}

                {/* Contenido principal: Tabla */}
                {!hasData && !state.loading ? (
                    <div className="rounded-3xl border border-dashed border-gray-200 bg-white p-8 shadow-sm">
                        <EmptyState
                            title="No hay préstamos activos"
                            description="No se encontraron préstamos activos con los filtros seleccionados"
                            actionButton={
                                <button
                                    onClick={handleResetFilters}
                                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition transform hover:scale-105"
                                >
                                    Ver todos los préstamos
                                </button>
                            }
                        />
                    </div>
                ) : (
                    <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
                        <PrestamosActivosTable
                            rows={data}
                            loading={state.loading}
                            error={state.error}
                        />
                    </div>
                )}

                {/* Paginación */}
                {meta.total > 0 && meta.last_page > 1 && (
                    <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
                        <Paginacion
                            paginacion={{
                                pagina_actual: meta.current_page,
                                ultima_pagina: meta.last_page,
                                desde: meta.from,
                                hasta: meta.to,
                                total: meta.total
                            }}
                            alCambiarPagina={handlePageChange}
                            cargando={state.loading}
                            nombreItem="préstamos"
                            colorTema="red"
                        />
                    </div>
                )}

                {/* Información de filtros aplicados */}
                {hasData && (filters.asesor_id || filters.abonado_por || filters.casi_por_terminar) && (
                    <div className="mt-4 text-center text-sm text-gray-600">
                        {filters.asesor_id && (
                            <>Asesor: <strong>{getAsesorName()}</strong></>
                        )}
                        {filters.abonado_por && (
                            <>{filters.asesor_id ? ' · ' : ''}Abonado por: <strong>{filters.abonado_por}</strong></>
                        )}
                        {filters.casi_por_terminar && (
                            <>{(filters.asesor_id || filters.abonado_por) ? ' · ' : ''}Mostrando solo <strong>casi por terminar</strong></>
                        )}
                    </div>
                )}

                {/* Toast de error */}
                {hasError && hasData && (
                    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
                        <div className="bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3">
                            <div className="flex-1">
                                <p className="text-sm font-medium">Error al actualizar</p>
                                <p className="text-xs opacity-90">{state.error}</p>
                            </div>
                            <button
                                onClick={handleDismissError}
                                className="text-white hover:bg-red-700 rounded p-1"
                                aria-label="Cerrar"
                            >
                                ✕
                            </button>
                        </div>
                    </div>
                )}

                {/* Modal de PDF */}
                {showPDFModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        {pdfDataRef.current.isGenerating ? (
                            <ReportePrestamosActivos
                                loans={pdfDataRef.current.loans}
                                filters={filters}
                                asesores={asesores}
                                totalRegistros={pdfDataRef.current.loans.length}
                                onPDFGenerated={handlePDFGenerated}
                            />
                        ) : (
                            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh] flex flex-col">
                                <div className="flex items-center justify-between p-4 border-b">
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        Reporte de Préstamos Activos
                                    </h3>
                                    <div className="flex gap-2">
                                        <a
                                            href={pdfDataRef.current.url}
                                            download={`reporte-prestamos-activos-${new Date().toISOString().split('T')[0]}.pdf`}
                                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition"
                                        >
                                            Descargar PDF
                                        </a>
                                        <button
                                            onClick={handleClosePDF}
                                            className="text-gray-500 hover:text-gray-700 p-2"
                                            aria-label="Cerrar"
                                        >
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <div className="flex-1 overflow-auto">
                                    <iframe
                                        src={pdfDataRef.current.url}
                                        className="w-full h-full"
                                        title="Vista previa del PDF"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Componente de generación de Excel */}
                {excelDataRef.current.isGenerating && (
                    <ReportePrestamosActivosExcel
                        loans={excelDataRef.current.loans}
                        filters={filters}
                        asesores={asesores}
                        totalRegistros={excelDataRef.current.loans.length}
                        onExcelGenerated={handleExcelGenerated}
                    />
                )}
            </div>
        </div>
    );
}